import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../../_common/application/ts/Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { Constants } from '../../../../pdc-widget-gauge/src/ts/stores/Constants';
import { Dispatcher } from './Dispatcher';
import { Actions, I, checkAction } from './GaugeActions';

import GlobalState from '../../../../_common/platform/ts/globalStates/GlobalState';
import { NullHandling, useReplacingType } from '../../../../_common/platform/ts/nullHandling';
import { themeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';
import * as DateTimeFunctions from '../../../../_common/platform/ts/DateTimeFunctions';
import * as Globalization from '../../../../_common/platform/ts/Globalization';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';

import { IAQLParameters, IDataParameters } from '../../../../_common/platform/ts/AqlUtility';

import {
    IAttributeName,
    IGlobalFilterControl,
    IQueryFilter
} from '../../../../_common/platform/tsx/filtering/Interfaces';

import { IWidgetOptions } from '../../../../pdc-platform/src/ts/Interfaces';
import { ATTRIBUTE_SEPARATOR } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';

export interface IGaugeState {
    data: any[];
    scaleLimits: any;
    globalState: GlobalState;
    ageFromBackend: number;
    datetimeOfAge: Date;
    options: IWidgetOptions;
}

interface IGaugeObject extends IAttributeName {
    dataSourceID: number;
    value?: string;
    agregate?: string;
    queryRootEntity?: string;
    distinct?: boolean;
    globalFilterControl?: IGlobalFilterControl;
    filter?: IQueryFilter[];
    subValueType: string;
    label?: string;
}

interface IGaugeDataPerameters extends IDataParameters {
    ref?: string;
}

export class GaugeStore extends BaseStore<IGaugeState, Actions, Dispatcher, IGlob> {
    public _state: IGaugeState = GaugeStore.getInitialState();
    public static _name = 'gauge-store';

    private static getInitialState() {
        return {
            data: [],
            scaleLimits: {},
            globalState: null,
            ageFromBackend: 0,
            datetimeOfAge: new Date(),
            errors: null,
            options: {}
        };
    }

    constructor(glob, dispatcher: Dispatcher, actions: any, options: IWidgetOptions, state: GlobalState) {
        super(glob, dispatcher, actions, GaugeStore._name, true);

        this._state.globalState = state;
        this._state.options = options;
    }

    public getRunTimeWidgetID() {
        return this._state.options.runTimeWidgetID;
    }

    public getScopeOptions() {
        return this._state.options.scopeOptions;
    }

    // VR: it should be PUBLIC here for JEST:
    public getGaugeData(
        config: any,
        id: number,
        cacheParams: IAQLParameters,
        theme: IValuesCollection,
        cultureValues: Globalization.ICultureValues
    ) {
        const runTimeWidgetID = this.getRunTimeWidgetID();

        if (runTimeWidgetID !== id) {
            return;
        }

        this.glob.AppActions.cleanWidgetNotifications(runTimeWidgetID);

        const globalState = this._state.globalState;
        const timeZone = DateTimeFunctions.getTimeZoneFromTheme(theme);
        const nullHandler = new NullHandling(theme);
        const scopeOptions = this.getScopeOptions();
        const values = config.values;
        const kpiArray: IGaugeObject[] = [];

        for (let i = 0; i < values.length; i++) {
            const attrib = values[i];
            const res: IGaugeObject = {
                agregate: attrib.attribute.aggregateGroup,
                distinct: attrib.attribute.distinct,
                filter: attrib.filter,
                globalFilterControl: attrib.globalFilterControl,
                dataSourceID: attrib.dataSourceID,
                queryRootEntity: attrib.queryRootEntity,
                subValueType: attrib.attribute.subValueType,
                label: attrib.attribute.label
            };

            if (attrib.attribute.attribute != null) {
                res.attribute = attrib.attribute.attribute.split(ATTRIBUTE_SEPARATOR);
            } else if (attrib.attribute.expression != null) {
                res.expression = attrib.attribute.expression;
            }

            kpiArray.push(res);
        }

        $.when
            .apply(
                $,
                $.map(kpiArray, (KPI) => {
                    const { dataSourceID, queryRootEntity } = KPI;
                    const def = $.Deferred();

                    globalState
                        .makeFiltersForWidget(
                            dataSourceID,
                            {
                                filters: KPI.filter,
                                globalFilterControl: KPI.globalFilterControl
                            },
                            runTimeWidgetID,
                            timeZone,
                            theme,
                            cultureValues,
                            scopeOptions
                        )
                        .done((filter) => {
                            let attr: any = { agregate: 'none' };

                            if (KPI.attribute != null) {
                                attr = {
                                    attrib: KPI.attribute
                                };
                            } else if (KPI.expression != null) {
                                const boundExpr = globalState.expressionBindVariables(
                                    KPI.expression,
                                    dataSourceID,
                                    runTimeWidgetID,
                                    scopeOptions,
                                    cultureValues
                                );

                                if (boundExpr == null) {
                                    return def.resolve();
                                }

                                attr = {
                                    syntaxTree: boundExpr.syntaxTree,
                                    rettype: boundExpr.rettype
                                };
                            }

                            if (KPI.agregate != null && KPI.agregate !== 'none') {
                                attr.agregate = KPI.agregate;
                            }

                            const args: IDataParameters = {
                                ...cacheParams,
                                attrib_refs: [attr],
                                limit: 0,
                                distinct: KPI.distinct,
                                group_attribs: [],
                                filter: filter,
                                dataSourceID: dataSourceID,
                                queryRootEntity: queryRootEntity
                            };

                            args.nullHandling = nullHandler.getBackendHandling();

                            this.glob.AQLQueries.loadData(args, this._state.options)
                                .done((data) => {
                                    const rn = theme[themeVars.nullHandling_replaceForNumeric];
                                    const value =
                                        data.result != null && data.result[0] != null ? data.result[0][0] : null;

                                    if (
                                        rn != null &&
                                        rn.replacingType != null &&
                                        rn.replacingType == useReplacingType.USE_OWN
                                    ) {
                                        KPI.value = nullHandler.handleNulls(value, data.typeList[0]);
                                    } else {
                                        KPI.value = value;
                                    }

                                    this._state.ageFromBackend = data.age;
                                    this._state.datetimeOfAge = new Date();

                                    def.resolve();
                                })
                                .fail(() => {
                                    def.reject();
                                });
                        })
                        .fail(() => {
                            def.reject();
                        });

                    return def;
                })
            )
            .done(() => {
                this.getGaugeScaleLimits(
                    config.gaugeFromToField,
                    (scaleLimits) => {
                        this._state.scaleLimits = scaleLimits;
                        this.actions.gaugeGetDataFinish(kpiArray, id);
                    },
                    cacheParams,
                    timeZone,
                    nullHandler,
                    theme,
                    cultureValues
                );
            });
    }

    private getGaugeDataFinish(result, id: number) {
        if (this.getRunTimeWidgetID() !== id || this._state.data == null) {
            return;
        }

        this._state.data = result;
    }

    private getLimitLookupAttref(valueLookup) {
        let result;

        if (valueLookup.attribute != null) {
            let attrSplitted;

            if (_.isArray(valueLookup.attribute)) {
                attrSplitted = valueLookup.attribute[0].split(ATTRIBUTE_SEPARATOR);
            } else {
                attrSplitted = valueLookup.attribute.split(ATTRIBUTE_SEPARATOR);
            }

            result = {
                attrib: attrSplitted,
                agregate: valueLookup.aggregateGroup
            };
        } else if (valueLookup.expression != null) {
            result = {
                syntaxTree: valueLookup.expression.syntaxTree,
                rettype: valueLookup.expression.rettype,
                agregate: valueLookup.aggregateGroup
            };
        }

        return result;
    }

    private getGaugeScaleLimits(
        gaugeFromToField: any,
        callback,
        cacheParams: IAQLParameters,
        timeZone: DateTimeFunctions.ITimeZone,
        nullHandler: NullHandling,
        theme: IValuesCollection,
        cultureValues: Globalization.ICultureValues
    ) {
        const result = {
            startValue: 0,
            endValue: 100
        };

        const dataSourceID = gaugeFromToField.dataSourceID;
        const globalState = this._state.globalState;
        const runTimeWidgetID = this.getRunTimeWidgetID();
        const scopeOptions = this.getScopeOptions();

        let args = [];

        if (gaugeFromToField.startValueExplicit) {
            result.startValue = gaugeFromToField.startValue;
        }

        if (gaugeFromToField.endValueExplicit) {
            result.endValue = gaugeFromToField.endValue;
        }

        if (!gaugeFromToField.startValueExplicit) {
            const startValueLookup = gaugeFromToField.startValueLookup;

            if (
                startValueLookup != null &&
                (startValueLookup.attribute != null || startValueLookup.expression != null)
            ) {
                globalState
                    .makeFiltersForWidget(
                        dataSourceID,
                        {
                            filters: startValueLookup.filters,
                            globalFilterControl: startValueLookup.globalFilterControl
                        },
                        runTimeWidgetID,
                        timeZone,
                        theme,
                        cultureValues,
                        scopeOptions
                    )
                    .done((filter) => {
                        const attr_ref = this.getLimitLookupAttref(startValueLookup);
                        const argsStart: IGaugeDataPerameters = {
                            ...cacheParams,
                            ref: 'startValue',
                            attrib_refs: [attr_ref],
                            limit: 0,
                            distinct: startValueLookup.distinct,
                            group_attribs: [],
                            filter: filter,
                            dataSourceID: dataSourceID,
                            nullHandling: nullHandler.getBackendHandling()
                        };

                        args.push(argsStart);
                    });
            }
        }

        if (!gaugeFromToField.endValueExplicit) {
            const endValueLookup = gaugeFromToField.endValueLookup;

            if (endValueLookup != null && (endValueLookup.attribute != null || endValueLookup.expression != null)) {
                globalState
                    .makeFiltersForWidget(
                        dataSourceID,
                        {
                            filters: endValueLookup.filters,
                            globalFilterControl: endValueLookup.globalFilterControl
                        },
                        runTimeWidgetID,
                        timeZone,
                        theme,
                        cultureValues,
                        scopeOptions
                    )
                    .done((filter) => {
                        const attr_ref = this.getLimitLookupAttref(endValueLookup);
                        const argsEnd: IGaugeDataPerameters = {
                            ...cacheParams,
                            ref: 'endValue',
                            attrib_refs: [attr_ref],
                            limit: 0,
                            distinct: endValueLookup.distinct,
                            group_attribs: [],
                            filter: filter,
                            dataSourceID: dataSourceID,
                            nullHandling: nullHandler.getBackendHandling()
                        };

                        args.push(argsEnd);
                    });
            }
        }

        $.when
            .apply(
                $,
                $.map(args, (arg) => {
                    const def = $.Deferred();

                    $.when(
                        this.glob.AQLQueries.loadData(arg, this._state.options)
                    ).then((data) => {
                        result[arg.ref] = data.result[0][0]; //nullHandler.handleNulls(data.result[0][0],data.typeList[0]);

                        this._state.ageFromBackend = data.age;
                        this._state.datetimeOfAge = new Date();

                        def.resolve();
                    });

                    return def;
                })
            )
            .done(function () {
                callback(result);
            });
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const action = payload.action;

        if (checkAction<I.GET_GAUGE_DATA>(action, Constants.GET_GAUGE_DATA)) {
            this.getGaugeData(action.config, action.id, action.cacheParams, action.theme, action.cultureValues);
            //this.emitChange();
        } else if (checkAction<I.GET_GAUGE_DATA_FINISH>(action, Constants.GET_GAUGE_DATA_FINISH)) {
            this.getGaugeDataFinish(action.result, action.id);
            this.emitChange();
        }

        return true;
    }

    static get(glob, dispatcher: Dispatcher, actions, options: IWidgetOptions, state: GlobalState) {
        return new GaugeStore(glob, dispatcher, actions, options, state);
    }
}
